/**
 * Minimal ZIP reader for `.npz` archives — the inverse of
 * `organism.observatory.artifacts.write_npz_artifact` (`numpy.savez`, which
 * uses `zipfile.ZIP_STORED`, i.e. uncompressed members, unless the writer
 * ever switches to `savez_compressed`). We support both `STORED` (method 0)
 * and `DEFLATE` (method 8) members so a future switch to `savez_compressed`
 * does not silently break this reader.
 *
 * Reads the End Of Central Directory record to find the central directory,
 * then each central directory header to locate every member's local file
 * header and compressed bytes — the standard way to read a zip without a
 * full streaming parser (`.npz` files here are at most a few hundred KB).
 */

import { parseNpy, type NpyArray } from "./npy";

export class NpzParseError extends Error {}

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_DIR_SIGNATURE = 0x02014b50;
const LOCAL_FILE_SIGNATURE = 0x04034b50;

function findEndOfCentralDirectory(view: DataView): number {
  // The EOCD record is at least 22 bytes and can be followed by a comment of
  // up to 65535 bytes, so scan backwards from the end.
  const maxScan = Math.min(view.byteLength, 65535 + 22);
  for (let offset = view.byteLength - 22; offset >= view.byteLength - maxScan; offset -= 1) {
    if (offset < 0) break;
    if (view.getUint32(offset, true) === EOCD_SIGNATURE) return offset;
  }
  throw new NpzParseError("not a valid .npz/zip archive: no End Of Central Directory record");
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const stream = new DecompressionStream("deflate-raw");
  const writer = stream.writable.getWriter();
  // Re-wrapped so the buffer's type parameter is concretely `ArrayBuffer`
  // (never `SharedArrayBuffer`) — satisfies `WritableStreamDefaultWriter`'s
  // `BufferSource` typing under TS's stricter typed-array generics (5.7+).
  void writer.write(new Uint8Array(bytes));
  void writer.close();
  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) chunks.push(value);
  }
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let position = 0;
  for (const chunk of chunks) {
    out.set(chunk, position);
    position += chunk.length;
  }
  return out;
}

/** Parses an `.npz` archive into its named `.npy` members. */
export async function parseNpz(buffer: ArrayBuffer): Promise<Record<string, NpyArray>> {
  const view = new DataView(buffer);
  const eocdOffset = findEndOfCentralDirectory(view);
  const entryCount = view.getUint16(eocdOffset + 10, true);
  const centralDirOffset = view.getUint32(eocdOffset + 16, true);

  const result: Record<string, NpyArray> = {};
  let cursor = centralDirOffset;
  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(cursor, true) !== CENTRAL_DIR_SIGNATURE) {
      throw new NpzParseError(`corrupt central directory entry at offset ${cursor}`);
    }
    const compressionMethod = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localHeaderOffset = view.getUint32(cursor + 42, true);
    const nameBytes = new Uint8Array(buffer, cursor + 46, nameLength);
    let name = new TextDecoder("utf-8").decode(nameBytes);
    if (name.endsWith(".npy")) name = name.slice(0, -4);

    // Local file header: read its own name/extra lengths since they may
    // differ in padding from the central directory record.
    if (view.getUint32(localHeaderOffset, true) !== LOCAL_FILE_SIGNATURE) {
      throw new NpzParseError(`corrupt local file header for member ${name}`);
    }
    const localNameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + localNameLength + localExtraLength;
    const compressedBytes = new Uint8Array(buffer, dataStart, compressedSize);

    let memberBytes: Uint8Array;
    if (compressionMethod === 0) {
      memberBytes = compressedBytes;
    } else if (compressionMethod === 8) {
      memberBytes = await inflateRaw(compressedBytes);
    } else {
      throw new NpzParseError(
        `unsupported zip compression method ${compressionMethod} for ${name}`,
      );
    }
    result[name] = parseNpy(
      memberBytes.buffer.slice(
        memberBytes.byteOffset,
        memberBytes.byteOffset + memberBytes.byteLength,
      ) as ArrayBuffer,
    );

    cursor += 46 + nameLength + extraLength + commentLength;
  }
  return result;
}
